import { Version } from "@app/shared/models/version.model";

export interface BuySellPlan {
    id: string;
    name: string;
    description: string;
    isActive: boolean;
    maxBuyHours: number;
    maxSellHours: number;
    minSellHours: number;
    buySellIncrementHours: number;
    effectiveDate: Date;
    createdBy: string;
    createdDate: Date;
    modifiedBy?: string;
    modifiedDate?: Date;
    version: Version;
}

export interface LocalizedText {
    culture: string;
    text: string;
}

export interface Status {
    id: string;
    name: string;
}

export interface TimeOffType {
    id: string;
    name: string;
}

export interface BuySellPlanVerbose {
    id: string;
    name: LocalizedText[];
    description: LocalizedText[];
    status: Status;
    prorateOnFte: boolean;
    permitSellingNegativeNumbers: boolean;
    displayFutureAccrual: boolean;
    maximumHoursEmployeeCanSell: number;
    maximumHoursEmployeeCanBuy: number;
    timeOffType: TimeOffType;
    hoursPerPeriodSalary: number;
    hoursPerPeriodMonthly: number;
    hoursPerPeriodWeekly: number;
    hoursPerPeriodDaily: number;
    allowBuy: boolean;
    allowSell: boolean;
    publishSubject: LocalizedText[];
    publishBody: LocalizedText[];
    approverSubject: LocalizedText[];
    approverBody: LocalizedText[];
    approvedSubject: LocalizedText[];
    approvedBody: LocalizedText[];
    deniedSubject: LocalizedText[];
    deniedBody: LocalizedText[];
    finalApprovedBuySubject: LocalizedText[];
    finalApprovedBuyBody: LocalizedText[];
    finalApprovedSellSubject: LocalizedText[];
    finalApprovedSellBody: LocalizedText[];
    version: Version;
}

export interface BuySellPlanEmployee {
    id: string;
    employee: {
        id: string;
        firstName: string;
        lastName: string;
        customClientId: string;
        status: Status;
    };
    plan: {
        id: string;
        name: string;
    };
    published: boolean;
    publishedOn: Date;
    publishedBy: {
        id: string;
        firstName: string;
        lastName: string;
    };
    status: {
        id: string;
        name: string;
    }
    maximumBuyHours: number;
    maximumSellHours: number;
    hours: number;
    hourlyCost: number;
    futureAccruals: {
        date: string; // ISO date string
        amount: number;
    }[]
    type: {
        id: string;
        name: string;
    }
    reviewers: BuySellPlanReviewer[] | { id: string, firstName: string, lastName: string }[];
    version: Version;
}

export interface BuySellPlanReviewer {
    id: string;
    plan: {
        id: string;
        name: string;
    };
    reviewerType: {
        id: string;
        name: string;
    };
    reviewer: {
        id: string;
        firstName: string;
        lastName: string;
    };
    orderNumber: number;
    version: Version;
}

export interface BuySellPlanReviewerSubmit {
    changeReason: string;
    changeReasonComments: string;
    originalCreatedOn: string; // ISO date string
    asOf: string; // ISO date string
    reviewerType: {
        id: string;
    };
    reviewer: {
        id: string;
    };
    orderNumber: number;
    plan: {
        id: string;
    };
}

export interface BuySellPlanPublishHistory {
    id: string;
    publishedOn: Date;
    publishedBy: {
        id: string;
        firstName: string;
        lastName: string;
    };
    plan: {
        id: string;
        name: string;
    };
    startedAt: Date;
    completedAt: Date;
}

export interface BuySellApproval {
    id: string;
    employee: {
        id: string;
        firstName: string;
        lastName: string;
    };
    orderNumber: number;
    isApproved: boolean;
    comments: string;
    plan: {
        id: string;
        name: string;
    };
    status: {
        id: string;
        text: string;
    };
    reviewer: {
        id: string;
        firstName: string;
        lastName: string;
    };
    buySell: {
        type: {
            id: string;
            name: string;
        };
        hours: number;
        hourlyCost: number;
    };
    version: Version;
}

export interface BuySellHoursReportItem {
    id: string;
    employee: {
        id: string;
        firstName: string;
        lastName: string;
        customClientId: string;
        status: {
            id: string;
            name: string;
        };
        payrollNumber: string;
        hoursPerWeek: number;
    };
    plan: {
        id: string;
        name: string;
    };
    published: boolean;
    publishedOn: Date;
    publishedBy: {
        id: string;
        firstName: string;
        lastName: string;
    };
    version: {
        createdOn: Date;
        modifiedList: string[];
        changeReason: {
            id: string;
            text: string;
        };
        comments: string;
        createdBy: {
            id: string;
            firstName: string;
            lastName: string;
        };
    };
    status: {
        id: string;
        name: string;
    };
    maximumBuyHours: number;
    maximumSellHours: number;
    hours: number;
    hourlyCost: number;
    type: {
        id: string;
        name: string;
    };
    created: {
        on: Date;
        by: {
            id: string;
            firstName: string;
            lastName: string;
        };
    };
    reviewers: {
        id: string;
        orderNumber: number;
        isApproved: boolean;
        comments: string;
        version: {
            createdOn: Date;
            modifiedList: string[];
            changeReason: {
                id: string;
                text: string;
            };
            comments: string;
            createdBy: {
                id: string;
                firstName: string;
                lastName: string;
            };
        };
        status: {
            id: string;
            text: string;
        };
        reviewer: {
            id: string;
            firstName: string;
            lastName: string;
        };
        created: {
            on: Date;
            by: {
                id: string;
                firstName: string;
                lastName: string;
            };
        };
    }[];
    customClientId?: string;
    userId?: string;
    payrollNumber?: string;
    hoursPerWeek?: number;
    totalCost?: number;
    totalCostPerDay?: number;
    totalCostPerWeek?: number;
    totalCostPerMonth?: number;
    totalCostPerQuarter?: number;
}